/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaClock, FaCheckCircle, FaSpinner, FaFilePdf, FaFileWord, FaTrash, FaUser, FaEye, FaTimes, FaCalendarAlt, FaStar, FaLanguage, FaTrophy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as articleService from '../services/articleService';
import * as masterService from '../services/masterService';

const MySubmittedArticlesYoungIndia = () => {
  const [articles, setArticles] = useState([]);
  const [masterData, setMasterData] = useState({
    subjects: [],
    streams: [],
    specializations: []
  });
  const [toast, setToast] = useState(null);
  const [showReviewCommentModal, setShowReviewCommentModal] = useState(false);
  const [selectedReviewComment, setSelectedReviewComment] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedArticleTitle, setSelectedArticleTitle] = useState('');
  const navigate = useNavigate();

  const subjects = [
    'Computer Science',
    'Electronics',
    'Mechanical Engineering',
    'Civil Engineering',
    'Electrical Engineering',
    'Physics',
    'Chemistry',
    'Mathematics',
    'Biology',
    'Commerce',
    'Arts',
    'Other',
  ];

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        // 1. Fetch Master Data
        const [subjectsRes, streamsRes, specsRes] = await Promise.all([
          masterService.getAllSubjects(),
          masterService.getAllStreams(),
          masterService.getAllSpecializations()
        ]);

        // Safely extract data arrays
        const newMasterData = {
          subjects: subjectsRes.success ? subjectsRes.data : (Array.isArray(subjectsRes) ? subjectsRes : []),
          streams: streamsRes.success ? streamsRes.data : (Array.isArray(streamsRes) ? streamsRes : []),
          specializations: specsRes.success ? specsRes.data : (Array.isArray(specsRes) ? specsRes : [])
        };

        setMasterData(newMasterData);

        // 2. Fetch Articles
        const data = await articleService.getMySubmissions();

        // Handle both direct array and wrapped response formats
        const articlesArray = Array.isArray(data) ? data : (data?.submissions || data?.articles || data?.data || []);

        console.log('API Response for My Submissions:', data);
        console.log('Parsed articles array:', articlesArray);

        // Map and normalize data for UI consistency
        const mappedArticles = (Array.isArray(articlesArray) ? articlesArray : []).map(article => {
          // Resolve running title info if available (nested)
          const rt = article.running_title || {};

          // Resolve names from IDs using master data (using the locally fetched newMasterData)
          const resolvedSubject = newMasterData.subjects.find(s => s.id === article.subject_id)?.name;
          const resolvedStream = newMasterData.streams.find(s => s.id === article.stream_id)?.name;
          const resolvedSpec = newMasterData.specializations.find(s => s.id === article.specialization_id)?.name;

          // Extract file URL - check all possible field names
          const fileUrl = article.file_url ||
            article.fileUrl ||
            article.url ||
            article.file?.url ||
            article.file?.file_url ||
            null;

          // Create uploadedFile object if file URL exists
          let uploadedFile = null;
          if (fileUrl) {
            const fileName = fileUrl.split('/').pop();
            uploadedFile = {
              name: fileName || 'document',
              url: fileUrl
            };
          } else if (article.uploadedFile) {
            uploadedFile = article.uploadedFile;
          }

          // Determine content type
          let contentType = article.content_type || article.contentType;
          if (!contentType || contentType === 'unknown') {
            if (fileUrl || uploadedFile) {
              contentType = 'file';
            } else if (article.content_text || article.contentText || article.article_text || article.articleText) {
              contentType = 'text';
            } else {
              contentType = 'unknown';
            }
          }

          const mapped = {
            ...article,
            // Article Title
            students: (article.students && article.students.length > 0) ? article.students :
              (article.authors && article.authors.length > 0) ? article.authors.map(a => ({ ...a, name: a.name || a.authorName || a.fullName })) :
                (article.submitter ? [{ name: article.submitter.full_name || article.submitter.name || 'Unknown', email: article.submitter.email }] : []),
            paperTitle: article.title || article.paper_title || article.paperTitle || rt.title || rt.articleTitle || 'Untitled Article',

            // Subject
            subject: resolvedSubject ||
              article.subject_name ||
              (article.subject?.name) ||
              (rt.subject?.name) ||
              article.subject ||
              'N/A',

            // Language
            articleLanguage: article.language || article.articleLanguage || 'N/A',

            // Stream
            stream: resolvedStream ||
              article.stream_name ||
              (article.stream?.name) ||
              (rt.stream?.name) ||
              article.stream ||
              '',

            // Specialization
            specialization: resolvedSpec ||
              article.specialization_name ||
              (article.specialization?.name) ||
              (rt.specialization?.name) ||
              article.specialization ||
              '',

            // Dates
            submittedAt: article.submission_date || article.submittedAt || article.created_at,

            // Content / File
            uploadedFile: uploadedFile,

            // Article Content
            articleText: article.content_text || article.contentText || article.article_text || article.articleText || '',
            contentType: contentType,
            file_url: fileUrl,
            university: article.university || 'N/A',
            resultDate: article.result_date || article.resultDate || rt.result_date || rt.resultDate || rt.declared_at || null,
            // Extract review info if review exists
            review: ((['reviewed', 'approved', 'checked'].includes((article.status || '').toLowerCase())) && article.reviews && article.reviews.length > 0) ? {
              rating: Number(article.reviews[0].rating),
              reviewComment: article.reviews[0].comments,
              reviewedAt: article.reviews[article.reviews.length - 1].created_at // Take the latest review date
            } : null
          };

          return mapped;
        });

        // Sort by submission date in descending order (newest first)
        const sortedArticles = mappedArticles.sort((a, b) => {
          const dateA = new Date(a.submittedAt || 0);
          const dateB = new Date(b.submittedAt || 0);
          return dateB - dateA;
        });

        setArticles(sortedArticles);
      } catch (error) {
        console.error('Error loading data:', error);
        showToast('Failed to load submitted articles', 'error');
        setArticles([]);
      }
    };

    fetchData();
  }, [navigate]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const getStatusConfig = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'submitted':
        return {
          label: 'Submitted',
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          icon: FaClock,
          borderColor: 'border-blue-500'
        };
      case 'in-review':
        return {
          label: 'In Review',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-700',
          icon: FaSpinner,
          borderColor: 'border-yellow-500'
        };
      case 'reviewed':
      case 'approved':
      case 'checked':
        return {
          label: 'Reviewed',
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          icon: FaCheckCircle,
          borderColor: 'border-green-500'
        };
      default:
        return {
          label: status || 'Unknown',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          icon: FaClock,
          borderColor: 'border-gray-500'
        };
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return null;
    const extension = fileName.split('.').pop().toLowerCase();
    if (extension === 'pdf') {
      return <FaFilePdf className="text-red-500 text-xl" />;
    } else if (extension === 'doc' || extension === 'docx') {
      return <FaFileWord className="text-blue-500 text-xl" />;
    }
    return <FaFileAlt className="text-gray-500 text-xl" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntilResult = (resultDate) => {
    if (!resultDate) return null;
    const now = new Date();
    const result = new Date(resultDate);

    const resultStart = new Date(result.getFullYear(), result.getMonth(), result.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = resultStart - nowStart;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isResultDatePassed = (resultDate) => {
    if (!resultDate) return false;
    const now = new Date();
    const result = new Date(resultDate);
    const resultDateOnly = new Date(result.getFullYear(), result.getMonth(), result.getDate());
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return resultDateOnly < nowDateOnly;
  };

  const handleReviewCommentClick = (article) => {
    setSelectedArticleTitle(article.paperTitle);

    if (article.review) {
      setSelectedReview(article.review);
      setSelectedReviewComment(article.review.reviewComment || '');
      setShowReviewCommentModal(true);
    }
  };

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Review Comment Modal */}
      {showReviewCommentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full animate-fade-in">
            <div className="p-6 rounded-t-2xl" style={darkGradient}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Review Details</h2>
                  <p className="text-blue-100">{selectedArticleTitle}</p>
                </div>
                <button
                  onClick={() => setShowReviewCommentModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              {/* Rating Display */}
              {/* Rating Display - Hidden per user request */}
              {/* {selectedReview && selectedReview.rating !== undefined && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Article Impact</h3>
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border-2 border-yellow-200">
                    <div className="flex items-center justify-center gap-3">
                      <FaStar className="text-4xl text-yellow-500" />
                      <div className="text-center">
                        <p className="text-5xl font-bold text-gray-900">{selectedReview.rating.toFixed(2)}</p>
                        <p className="text-gray-600 mt-1">out of 10</p>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-sm text-gray-600">
                        {selectedReview.rating >= 9 ? '🏆 Excellent!' :
                          selectedReview.rating >= 7.5 ? '🎉 Very Good!' :
                            selectedReview.rating >= 6 ? '👍 Good' :
                              '📝 Keep Improving'}
                      </p>
                    </div>
                  </div>
                </div>
              )} */}

              {/* Comments */}
              {selectedReviewComment && selectedReviewComment.trim() ? (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Reviewer's Feedback</h3>
                  <div className="border-l-4 p-4 rounded-r-lg bg-blue-50 border-blue-500">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedReviewComment}</p>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Reviewer's Feedback</h3>
                  <div className="border-l-4 p-4 rounded-r-lg bg-gray-50 border-gray-300">
                    <p className="text-gray-500 italic">No feedback provided.</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setShowReviewCommentModal(false)}
                  className="px-6 py-3 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                  style={darkGradient}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">My Submitted Articles - Young India</h1>
                <p className="text-blue-100">Track your article submissions and reviews</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaFileAlt className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Articles List */}
          {articles.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Articles Yet</h3>
              <p className="text-gray-600">You haven't submitted any articles for Young India competition.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {articles.map((article) => {
                const statusConfig = getStatusConfig(article.status);
                const StatusIcon = statusConfig.icon;
                const isReviewed = ['reviewed', 'approved', 'checked'].includes((article.status || '').toLowerCase());
                const daysUntilResult = getDaysUntilResult(article.resultDate);
                const resultPassed = isResultDatePassed(article.resultDate);

                return (
                  <div
                    key={article.id}
                    className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border-l-4 ${statusConfig.borderColor}`}
                  >
                    <div className="p-6">
                      {/* Status and Rating Badge */}
                      <div className="flex flex-wrap justify-between items-start mb-6">
                        {/* Left: Dates */}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          {article.submittedAt && (
                            <div className="flex items-center gap-1">
                              <FaCalendarAlt />
                              <span>Submitted: {formatDate(article.submittedAt)}</span>
                            </div>
                          )}

                          {isReviewed && article.review?.reviewedAt && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-md border border-green-200">
                              <FaCheckCircle className="text-xs" />
                              <span className="font-medium">Reviewed: {formatDate(article.review.reviewedAt)}</span>
                            </div>
                          )}

                          {/* Result Date - Show for all articles that have it */}
                          {article.resultDate && (
                            <div className="flex items-center gap-1">
                              <FaTrophy className={resultPassed ? 'text-gray-500' : 'text-purple-500'} />
                              <span className={resultPassed ? 'text-gray-500' : 'text-purple-700 font-semibold'}>
                                Result: {formatDate(article.resultDate)}
                                {!resultPassed && daysUntilResult !== null && daysUntilResult >= 0 && (
                                  <span className="ml-1 text-purple-600">
                                    ({daysUntilResult === 0 ? 'Today!' : `in ${daysUntilResult} day${daysUntilResult !== 1 ? 's' : ''}`})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Right: Status and Rating */}
                        <div className="flex items-center gap-3">
                          {/* Rating Badge */}
                          {/* Article Impact Badge */}
                          {/* {isReviewed && article.review && article.review.rating !== undefined && (
                            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold shadow-md">
                              <FaStar />
                              {article.review.rating.toFixed(2)}/10
                            </div>
                          )} */}

                          {/* Status Badge with Eye icon */}
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${statusConfig.bgColor} ${statusConfig.textColor} text-sm font-semibold`}>
                            <StatusIcon className={article.status === 'in-review' ? 'animate-spin' : ''} />
                            {statusConfig.label}

                            {isReviewed && article.review &&
                              article.review.reviewComment &&
                              article.review.reviewComment.trim() !== '' &&
                              article.review.reviewComment.trim().toLowerCase() !== 'no comments' && (
                                <button
                                  onClick={() => handleReviewCommentClick(article)}
                                  className="ml-2 text-blue-600 hover:text-blue-800 transition-colors"
                                  title="View review details"
                                >
                                  <FaEye />
                                </button>
                              )}
                          </div>
                        </div>
                      </div>

                      {/* Result Date Banner - Prominent display when approaching */}
                      {article.resultDate && !resultPassed && daysUntilResult !== null && daysUntilResult <= 7 && (
                        <div className={`mb-4 p-4 rounded-xl border-2 ${daysUntilResult === 0 ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-purple-400' :
                          daysUntilResult <= 3 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300' :
                            'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                          }`}>
                          <div className="flex items-center gap-3">
                            <FaTrophy className={`text-2xl ${daysUntilResult === 0 ? 'text-purple-600' :
                              daysUntilResult <= 3 ? 'text-orange-600' :
                                'text-purple-500'
                              }`} />
                            <div>
                              <p className="font-bold text-gray-900">
                                {daysUntilResult === 0 ? '🎉 Results Announced Today!' :
                                  daysUntilResult === 1 ? '⏰ Results Tomorrow!' :
                                    `📅 Results in ${daysUntilResult} days`}
                              </p>
                              <p className="text-sm text-gray-600">
                                {new Date(article.resultDate).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Main Info Row */}
                      <div className="flex flex-wrap gap-4 mb-4">
                        {/* Paper Title */}
                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg border-2 border-indigo-200 flex-[2_1_250px] min-w-[250px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Article Title</p>
                          <p className="font-semibold text-gray-900 text-sm line-clamp-2" title={article.paperTitle}>
                            {article.paperTitle}
                          </p>
                        </div>

                        {/* Article Language */}
                        {article.articleLanguage && (
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border-2 border-green-200 flex-[1_1_120px] min-w-[120px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Language</p>
                            <div className="flex items-center gap-2">
                              <FaLanguage className="text-green-600 text-sm" />
                              <span className="font-semibold text-green-700 text-sm">
                                {article.articleLanguage}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Subject */}
                        <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                          <p className="text-xs text-gray-500 mb-1 font-semibold">Department</p>
                          <p className="font-semibold text-gray-900 text-sm">{article.subject}</p>
                        </div>

                        {/* Stream */}
                        {article.stream && (
                          <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                            <p className="text-xs text-gray-500 mb-1 font-semibold">Stream</p>
                            <p className="font-semibold text-gray-900 text-sm">{article.stream}</p>
                          </div>
                        )}

                        {/* Specialization (Topic Name) - Hide if redundant with title */}
                        {article.specialization &&
                          article.specialization.trim() &&
                          article.specialization.toLowerCase() !== article.paperTitle.toLowerCase() && (
                            <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                              <p className="text-xs text-gray-500 mb-1 font-semibold">Specialization</p>
                              <p className="font-semibold text-gray-900 text-sm">{article.specialization}</p>
                            </div>
                          )}

                        {/* Content Type */}
                        {article.uploadedFile ? (
                          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 rounded-lg border-2 border-purple-200 flex-[1_1_180px] min-w-[180px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Content Type</p>
                            <div className="flex items-center gap-2">
                              {getFileIcon(article.uploadedFile.name)}
                              <div className="flex-1 min-w-0">
                                <a
                                  href={article.uploadedFile.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline truncate block"
                                  title={`View/Download: ${article.uploadedFile.name}`}
                                >
                                  {article.uploadedFile.name}
                                </a>
                              </div>
                            </div>
                          </div>
                        ) : article.articleText ? (
                          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 rounded-lg border-2 border-blue-200 flex-[1_1_150px] min-w-[150px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Content Type</p>
                            <div className="flex items-center gap-2">
                              <FaFileAlt className="text-blue-500 text-lg" />
                              <p className="text-sm font-semibold text-gray-900">Text</p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-3 rounded-lg flex-[1_1_150px] min-w-[150px]">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">Content Type</p>
                            <div className="flex items-center gap-2 text-gray-400">
                              <FaFileAlt className="text-lg" />
                              <p className="text-sm font-semibold">No Content</p>
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MySubmittedArticlesYoungIndia;